import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import "./Layout.css";
import logo from "../assets/img/amblem.png";
import { useAuth } from "../contexts/AuthContext";
import BrandLockup from "../components/brand/BrandLockup";
import { recordSiteVisit } from "../lib/siteVisit";

// Tanıtım sayfaları: ana sayfa, misyon, mobil ve web, topluluk ayrı sayfalar.
const NAV = [
  { to: "/", label: "Ana Sayfa", end: true },
  { to: "/misyonumuz", label: "Misyonumuz" },
  { to: "/mobil-ve-web", label: "Mobil ve Web" },
  { to: "/topluluk", label: "Topluluk" },
];

const itemClass = (active) =>
  `text-[15px] font-SemiBold px-3 py-2 rounded-xl transition-all duration-200 ${
    active
      ? "text-terakkiPrimaryDark bg-terakkiBrandMist/70"
      : "text-terakkiInk/70 hover:text-terakkiPrimaryDark hover:bg-terakkiBrandMist/50"
  }`;

const LoginIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-[18px] h-[18px] transition-transform duration-200 group-hover:translate-x-0.5"
    aria-hidden="true"
  >
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const Layout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { hasToken } = useAuth();
  const authHref = hasToken ? "/uygulama/profil" : "/giris";
  const authLabel = hasToken ? "Uygulamaya Git" : "Giriş Yap";

  // Sayfa değişince en üstten başla (bölüm bağlantısı değilse).
  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, behavior: "instant" });
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Tanıtım sayfası ziyaret sayacı (anonim, sayfa başına bir kez).
  useEffect(() => {
    recordSiteVisit(location.pathname);
  }, [location.pathname]);

  // Kaydırınca üst çubuk biraz daha belirgin.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (item) => {
    if (item.end) return location.pathname === item.to && !location.hash;
    return location.pathname.startsWith(item.to);
  };

  const onNavClick = (item) => (e) => {
    setMenuOpen(false);
    // Ana sayfadayken logo ve Ana Sayfa en üste kaydırır.
    if (location.pathname !== "/") return;
    if (item.to === "/") {
      e.preventDefault();
      window.history.replaceState(null, "", "/");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <main className="App layout-main">
      {/* Soft mint zemin + ortam blobları (auth ekranlarıyla aynı dil) */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-terakkiBrandCloud via-white to-terakkiBrandMist" />
      <div className="pointer-events-none fixed -left-40 -top-40 -z-10 h-[34rem] w-[34rem] rounded-full bg-terakkiBrandMist blur-3xl opacity-70" />
      <div className="pointer-events-none fixed -right-48 top-1/3 -z-10 h-[38rem] w-[38rem] rounded-full bg-terakkiBrandSoft/35 blur-3xl" />
      <div className="pointer-events-none fixed -bottom-48 left-1/4 -z-10 h-[32rem] w-[32rem] rounded-full bg-terakkiBrandMist/80 blur-3xl" />

      {/* Üst menü */}
      <header className="fixed top-0 left-0 right-0 z-40">
        <div className="mx-auto max-w-6xl px-4">
          <div
            className={`mt-4 flex items-center justify-between rounded-2xl border px-5 py-3 backdrop-blur-lg transition-all duration-300 ${
              scrolled
                ? "border-white/80 bg-white/85 shadow-lg shadow-terakkiPrimaryDark/10"
                : "border-transparent bg-white/40"
            }`}
          >
            <Link to="/" onClick={onNavClick(NAV[0])} className="flex items-center gap-3">
              <img src={logo} alt="Adım Adım Terakki" className="h-10 w-auto object-contain" />
              <BrandLockup size="sm" />
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((item) => (
                <NavLink key={item.label} to={item.to} end={item.end} onClick={onNavClick(item)} className={() => itemClass(isActive(item))}>
                  {item.label}
                </NavLink>
              ))}
              <span className="mx-2 h-6 w-px bg-terakkiBorder" aria-hidden="true" />
              <Link
                to={authHref}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-terakkiPrimary to-terakkiPrimaryDark px-4 py-2 text-[15px] font-SemiBold text-white ring-1 ring-white/20 shadow-lg shadow-terakkiPrimary/30 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-terakkiPrimary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 active:translate-y-0"
              >
                <LoginIcon />
                {authLabel}
              </Link>
            </nav>
            <button
              className="md:hidden flex h-10 w-10 items-center justify-center rounded-xl text-terakkiInk hover:bg-terakkiBrandMist/50"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menüyü Aç/Kapat"
              aria-expanded={menuOpen}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" />
                )}
              </svg>
            </button>
          </div>
          {menuOpen && (
            <div className="md:hidden mt-2 rounded-2xl border border-white/70 bg-white/90 backdrop-blur px-4 py-4 flex flex-col gap-1 shadow-lg shadow-terakkiPrimaryDark/10">
              {NAV.map((item) => (
                <NavLink key={item.label} to={item.to} end={item.end} onClick={onNavClick(item)} className={() => itemClass(isActive(item))}>
                  {item.label}
                </NavLink>
              ))}
              <span className="my-2 h-px w-full bg-terakkiBorder" aria-hidden="true" />
              <Link
                to={authHref}
                onClick={() => setMenuOpen(false)}
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-terakkiPrimary to-terakkiPrimaryDark px-4 py-2.5 text-base font-SemiBold text-white ring-1 ring-white/20 shadow-lg shadow-terakkiPrimary/30 transition-all duration-200 active:scale-[0.98]"
              >
                <LoginIcon />
                {authLabel}
              </Link>
            </div>
          )}
        </div>
      </header>
      <div className="layout-content">
        <Outlet />
      </div>

      {/* Alt bilgi */}
      <footer className="border-t border-terakkiBorder mt-auto">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 text-sm text-terakkiNeutral">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logo} alt="Terakki" className="h-6 w-auto object-contain" />
              <BrandLockup size="xs" />
            </div>
            <p>Manevi terakki yolculuğunuzda yanınızdayız.</p>
            <p className="text-terakkiNeutral/80">
              © {new Date().getFullYear()} adimadimterakki. Tüm hakları saklıdır.
            </p>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 md:justify-end md:items-start">
            {NAV.map((item) => (
              <Link key={item.label} to={item.to} onClick={onNavClick(item)} className="hover:text-terakkiPrimaryDark">
                {item.label}
              </Link>
            ))}
            <Link to="/gizlilik-sozlesmesi" className="hover:text-terakkiPrimaryDark">
              Gizlilik Sözleşmesi
            </Link>
          </nav>
        </div>
      </footer>
    </main>
  );
};

export default Layout;
